import type { FearGreedData, MotionPhase } from "@/lib/fear-greed";

interface ButtonDomeProps {
  data: FearGreedData;
  phase: MotionPhase;
  bucketChanged: boolean;
  onTap: () => void;
  onPointerDown?: () => void;
  onPointerUp?: () => void;
  ariaLabel: string;
  translatedBucket: string;
}

export default function ButtonDome({
  data,
  phase,
  bucketChanged,
  onTap,
  onPointerDown,
  onPointerUp,
  ariaLabel,
  translatedBucket,
}: ButtonDomeProps) {
  const isDown = phase === "pressing" || phase === "pressed";
  const isBouncing = phase === "releasing";

  /* ── LAYER 1: Wide metallic base plate ── */
  const baseStyle: React.CSSProperties = {
    width: "min(68vw, 300px)",
    aspectRatio: "1 / 1",
    borderRadius: "9999px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: `
      conic-gradient(from 160deg,
        hsl(220 6% 74%) 0%, hsl(220 4% 82%) 5%, hsl(220 5% 70%) 10%,
        hsl(220 4% 80%) 15%, hsl(220 6% 72%) 20%, hsl(220 4% 84%) 25%,
        hsl(220 5% 70%) 30%, hsl(220 4% 82%) 35%, hsl(220 6% 74%) 40%,
        hsl(220 4% 80%) 45%, hsl(220 5% 72%) 50%, hsl(220 4% 84%) 55%,
        hsl(220 6% 70%) 60%, hsl(220 4% 82%) 65%, hsl(220 5% 74%) 70%,
        hsl(220 4% 80%) 75%, hsl(220 6% 72%) 80%, hsl(220 4% 84%) 85%,
        hsl(220 5% 70%) 90%, hsl(220 4% 82%) 95%, hsl(220 6% 74%) 100%
      )`,
    boxShadow: isDown
      ? `0 4px 12px rgba(0,0,0,0.16), 0 2px 4px rgba(0,0,0,0.1),
         inset 0 1px 2px rgba(255,255,255,0.15), inset 0 -1px 2px rgba(0,0,0,0.06)`
      : `0 28px 70px rgba(0,0,0,0.25), 0 14px 28px rgba(0,0,0,0.12),
         0 5px 10px rgba(0,0,0,0.07), inset 0 2px 4px rgba(255,255,255,0.3),
         inset 0 -2px 6px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.04)`,
    transition: "box-shadow 200ms ease",
    position: "relative",
    animation: "dome-breathe 4s ease-in-out infinite",
  };

  /* ── Beveled rim highlight ── */
  const rimStyle: React.CSSProperties = {
    position: "absolute", inset: "2px", borderRadius: "9999px",
    border: "1px solid rgba(255,255,255,0.22)",
    borderBottom: "1px solid rgba(0,0,0,0.1)",
    pointerEvents: "none",
  };

  /* ── Machined edge ring ── */
  const machinedEdgeStyle: React.CSSProperties = {
    position: "absolute", inset: "6px", borderRadius: "9999px",
    border: "1px solid rgba(0,0,0,0.08)",
    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
    pointerEvents: "none",
  };

  /* ── LAYER 2: Recessed channel ── */
  const channelStyle: React.CSSProperties = {
    width: "82%", height: "82%", borderRadius: "9999px",
    display: "flex", alignItems: "center", justifyContent: "center",
    background: `radial-gradient(ellipse at 50% 50%,
      hsl(220 12% 11%) 0%, hsl(220 10% 14%) 40%,
      hsl(220 8% 18%) 70%, hsl(220 6% 22%) 100%)`,
    boxShadow: `inset 0 8px 22px rgba(0,0,0,0.65), inset 0 -4px 10px rgba(0,0,0,0.4),
      inset 0 0 0 1px rgba(0,0,0,0.35), inset 0 0 3px 1px rgba(0,0,0,0.25)`,
    padding: isDown ? "3px 3px 0 3px" : "0 3px 6px 3px",
    transition: isDown
      ? "padding 120ms cubic-bezier(0.4,0,0.2,1)"
      : "padding 320ms cubic-bezier(0.34,1.56,0.64,1)",
    position: "relative",
  };

  /* ── Gasket ring ── */
  const gasketStyle: React.CSSProperties = {
    position: "absolute", inset: "4px", borderRadius: "9999px",
    border: "2px solid rgba(30,30,40,0.6)",
    boxShadow: "inset 0 1px 3px rgba(0,0,0,0.4), 0 0 1px rgba(255,255,255,0.05)",
    pointerEvents: "none",
  };

  /* ── LAYER 3: Glossy convex dome ── */
  const domeTransform = isDown
    ? "translateY(8px) scale(0.965)"
    : isBouncing
    ? "translateY(-3px) scale(1.012)"
    : "translateY(0) scale(1)";

  const domeTransition = isDown
    ? "transform 120ms cubic-bezier(0.4,0,0.2,1), filter 120ms ease, box-shadow 120ms ease, background 500ms ease"
    : "transform 320ms cubic-bezier(0.34,1.56,0.64,1), filter 280ms ease, box-shadow 320ms cubic-bezier(0.34,1.56,0.64,1), background 500ms ease";

  const crescentPos = isDown ? "32% 28%" : "22% 12%";
  const crescentPos2 = isDown ? "28% 25%" : "28% 18%";

  const domeStyle: React.CSSProperties = {
    width: "92%", height: "92%", borderRadius: "9999px",
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    gap: "2px",
    background: isDown
      ? `radial-gradient(ellipse 30% 22% at ${crescentPos}, rgba(255,255,255,0.14), transparent),
         radial-gradient(ellipse 60% 20% at 50% 85%, rgba(0,0,0,0.35), transparent),
         radial-gradient(ellipse 100% 15% at 50% 75%, rgba(255,255,255,0.03), transparent),
         radial-gradient(ellipse at 50% 42%, ${data.color}, ${data.color})`
      : `radial-gradient(ellipse 25% 16% at ${crescentPos}, rgba(255,255,255,0.7), transparent),
         radial-gradient(ellipse 12% 8% at ${crescentPos2}, rgba(255,255,255,0.45), transparent),
         radial-gradient(ellipse 55% 20% at 50% 85%, rgba(0,0,0,0.28), transparent),
         radial-gradient(ellipse 100% 12% at 50% 72%, rgba(255,255,255,0.06), transparent),
         radial-gradient(ellipse at 50% 45%, ${data.color}, ${data.color})`,
    boxShadow: isDown
      ? `inset 0 3px 8px rgba(255,255,255,0.03), inset 0 -8px 20px rgba(0,0,0,0.35),
         0 -1px 3px rgba(0,0,0,0.2), inset 0 0 0 1px rgba(255,255,255,0.04)`
      : isBouncing
      ? `inset 0 10px 28px rgba(255,255,255,0.24), inset 0 -16px 36px rgba(0,0,0,0.24),
         0 16px 32px rgba(0,0,0,0.26), 0 6px 12px rgba(0,0,0,0.16),
         inset 0 0 0 1px rgba(255,255,255,0.08)`
      : `inset 0 10px 28px rgba(255,255,255,0.22), inset 0 -16px 36px rgba(0,0,0,0.28),
         0 12px 28px rgba(0,0,0,0.24), 0 5px 10px rgba(0,0,0,0.15),
         inset 0 0 0 1px rgba(255,255,255,0.06)`,
    transform: domeTransform,
    filter: isDown ? "brightness(0.92) saturate(1.14)" : "brightness(1.05) saturate(1.1)",
    transition: domeTransition,
    cursor: "pointer",
    userSelect: "none",
    position: "relative",
  };

  /* ── Fresnel edge glow ── */
  const fresnelStyle: React.CSSProperties = {
    position: "absolute", inset: "0", borderRadius: "9999px",
    border: isDown ? "1px solid rgba(255,255,255,0.03)" : "1px solid rgba(255,255,255,0.1)",
    boxShadow: isDown ? "none" : "inset 0 0 8px rgba(255,255,255,0.06)",
    pointerEvents: "none",
    transition: "border 200ms ease, box-shadow 200ms ease",
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onTap}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onKeyDown={(e) => e.key === "Enter" && onTap()}
      style={baseStyle}
      className="outline-none focus-visible:ring-2 focus-visible:ring-foreground/20 focus-visible:ring-offset-0 focus-visible:ring-offset-background rounded-full"
      aria-label={ariaLabel}
    >
      <div style={rimStyle} />
      <div style={machinedEdgeStyle} />

      <div style={channelStyle}>
        <div style={gasketStyle} />
        <div style={domeStyle}>
          <div style={fresnelStyle} />
          <span
            className={`text-5xl sm:text-6xl font-black text-overlay-foreground tracking-tight ${
              bucketChanged ? "animate-[glow-pulse_1s_ease-out]" : ""
            }`}
            style={{ textShadow: "0 2px 12px rgba(0,0,0,0.4), 0 1px 4px rgba(0,0,0,0.25)" }}
          >
            {data.value}
          </span>
          <span
            className="text-[10px] sm:text-[11px] font-bold text-overlay-foreground/85 tracking-[0.2em] uppercase"
            style={{ textShadow: "0 1px 4px rgba(0,0,0,0.25)" }}
          >
            {translatedBucket}
          </span>
        </div>
      </div>
    </div>
  );
}
